import type { GameState, TurnFlags, PlayerState, GameEvent } from "@/types/game";
import { tickStatusesBySource, isStunned, poisonAmount } from "./Status";
import { finalizeDyingPlayer } from "./dying";
import { effectiveHandLimit } from "./Equipment";
import { Rng } from "@/utils/rng";

// 默认每回合摸牌数 / 手牌上限
export const DEFAULT_DRAW_COUNT = 2;
export const DEFAULT_HAND_LIMIT = 4;

// 初始化回合标记
export function freshTurnFlags(): TurnFlags {
  return {
    hasDrawn: false,
    hasDiscarded: false,
    drawCount: 0,
    attacksPlayed: 0,
    anyCardPlayed: false,
    zhushenComboAvailable: false,
  };
}

// 剧毒结算：所有中毒的活人损失生命（ss 规则：最多降至 1）
// 在每个玩家回合开始前调用
function tickPoison(state: GameState): GameState {
  let changed = false;
  const log = [...state.log];
  const players = state.players.map((p) => {
    if (!p.isAlive || p.isDying) return p;
    const dmg = poisonAmount(p);
    if (dmg <= 0) return p;
    changed = true;
    const actual = Math.min(dmg, Math.max(0, p.hp - 1));
    log.push({
      type: "damage",
      sourceId: p.id,
      targetId: p.id,
      amount: actual,
      damageType: "ss",
    });
    return { ...p, hp: Math.max(1, p.hp - dmg) };
  });
  if (!changed) return state;
  return { ...state, players, log };
}

// 进入某玩家的回合：phase=draw, 重置 turnFlags，重置 hasPlayedAttackThisTurn
export function beginTurn(state: GameState, playerIdx: number): GameState {
  const players = state.players.map((p, i) =>
    i === playerIdx
      ? {
          ...p,
          hasPlayedAttackThisTurn: false,
          hasUsedSkillThisTurn: false,
        }
      : p
  );
  return {
    ...state,
    currentPlayerIdx: playerIdx,
    phase: "draw",
    turnFlags: freshTurnFlags(),
    players,
  };
}

// 摸牌阶段：摸 n 张（默认 2）
export function performDraw(
  state: GameState,
  count: number = DEFAULT_DRAW_COUNT
): GameState {
  if (state.phase !== "draw") {
    throw new Error(`当前阶段不允许摸牌: ${state.phase}`);
  }
  const playerId = state.players[state.currentPlayerIdx].id;
  // 内联实现避免循环依赖 Deck.playerDraws
  const player = state.players[state.currentPlayerIdx];
  // 摸牌堆不足且弃牌堆有牌：弃牌堆重洗成新摸牌堆（保留摸牌堆残余在顶）
  let drawPile = state.drawPile;
  let discardPile = state.discardPile;
  let seed = state.seed;
  const extraLog: GameEvent[] = [];
  if (drawPile.length < count && discardPile.length > 0) {
    const rng = new Rng(seed);
    drawPile = [...drawPile, ...rng.shuffle(discardPile)];
    discardPile = [];
    seed = rng.snapshot();
    extraLog.push({ type: "reshuffle", count: drawPile.length });
  }
  const drawn = drawPile.slice(0, count);
  const rest = drawPile.slice(count);
  const newPlayer: PlayerState = {
    ...player,
    hand: [...player.hand, ...drawn],
  };
  const nextState: GameState = {
    ...state,
    seed,
    players: state.players.map((p, i) =>
      i === state.currentPlayerIdx ? newPlayer : p
    ),
    drawPile: rest,
    discardPile,
    turnFlags: {
      ...state.turnFlags,
      hasDrawn: true,
      drawCount: state.turnFlags.drawCount + drawn.length,
    },
    log: [
      ...state.log,
      ...extraLog,
      { type: "draw", playerId, count: drawn.length },
    ],
  };
  return { ...nextState, phase: "play" };
}

// 跳到弃牌阶段
export function enterDiscardPhase(state: GameState): GameState {
  if (state.phase !== "play") {
    throw new Error(`当前阶段不允许进入弃牌: ${state.phase}`);
  }
  return { ...state, phase: "discard" };
}

// 弃牌阶段：丢弃指定 uid 的牌至手牌上限
export function performDiscard(
  state: GameState,
  discardUids: number[]
): GameState {
  if (state.phase !== "discard") {
    throw new Error(`当前阶段不允许弃牌: ${state.phase}`);
  }
  const player = state.players[state.currentPlayerIdx];
  const toDiscard = player.hand.filter((c) => discardUids.includes(c.uid));
  const remaining = player.hand.filter(
    (c) => !discardUids.includes(c.uid)
  );
  const newPlayer: PlayerState = { ...player, hand: remaining };
  return {
    ...state,
    players: state.players.map((p, i) =>
      i === state.currentPlayerIdx ? newPlayer : p
    ),
    // 回合结束弃牌：拾遗不可取
    discardPile: [
      ...state.discardPile,
      ...toDiscard.map((c) => ({ ...c, discardReason: "turn-end" as const })),
    ],
    turnFlags: { ...state.turnFlags, hasDiscarded: true },
    log: [
      ...state.log,
      { type: "discard", playerId: player.id, count: toDiscard.length },
    ],
  };
}

// 回合结束：tick 玩家状态（剩余回合数 -1），跳到下一活人
export function endTurn(state: GameState): GameState {
  if (state.phase !== "discard" && state.phase !== "play") {
    throw new Error(`当前阶段不允许结束回合: ${state.phase}`);
  }
  // tick 由当前回合玩家释放的状态（挂在任何玩家身上都算）
  // 规则：状态在"释放者"的回合结束时 tick，而非携带者
  const curIdx = state.currentPlayerIdx;
  const curId = state.players[curIdx].id;
  let players = state.players.map((p) => tickStatusesBySource(p, curId));
  const cur = players[curIdx];
  let nextState: GameState = {
    ...state,
    players,
    phase: "turn-end",
    log: [...state.log, { type: "turn-end", playerId: cur.id } as const],
  };

  // 找到下一个活人；寒冰冻结的玩家跳过回合（其状态照常 tick 并毒发）
  // 濒死玩家：转一圈到自己回合开始时才结算死亡（给队友一圈救援机会）
  const n = players.length;
  let next = (curIdx + 1) % n;
  let guard = 0;
  while (guard <= n * 2) {
    const candidate = players[next];
    // 阵亡玩家：直接跳过
    if (!candidate.isAlive) {
      next = (next + 1) % n;
      guard++;
      continue;
    }
    // 濒死玩家：转一圈到该玩家回合开始时结算
    if (candidate.isDying) {
      if (candidate.hp > 0) {
        // 已被救援（hp > 0）：兜底清 isDying，正常进入回合
        players = players.map((p) =>
          p.id === candidate.id ? { ...p, isDying: false } : p
        );
        nextState = { ...nextState, players };
        break;
      }
      // hp <= 0：仍未获救 → 死亡/重生结算
      nextState = finalizeDyingPlayer(nextState, candidate.id);
      if (nextState.phase === "game-over") return nextState;
      players = nextState.players;
      // 重生成功：玩家恢复存活，正常进入其回合
      const after = players.find((p) => p.id === candidate.id)!;
      if (after.isAlive && after.hp > 0) break;
      // 真正阵亡：跳到下一位
      next = (next + 1) % n;
      guard++;
      continue;
    }
    if (isStunned(candidate)) {
      // 跳过其回合：状态 tick（解除冻结）+ 毒发 + 事件
      // 寒冰冻结：跳过回合的玩家依然 tick 他自己释放的状态
      const skipId = candidate.id;
      let skipState: GameState = {
        ...nextState,
        players: nextState.players.map((p) =>
          tickStatusesBySource(p, skipId)
        ),
        log: [...nextState.log, { type: "turn-skip", playerId: candidate.id }],
      };
      skipState = tickPoison(skipState);
      nextState = skipState;
      players = nextState.players;
      next = (next + 1) % n;
      guard++;
      continue;
    }
    break;
  }
  const newRound = next <= curIdx ? state.round + 1 : state.round;
  nextState = beginTurn(
    { ...nextState, round: newRound },
    next
  );
  // 回合开始前毒发（剧毒：每个玩家回合前损失生命）
  nextState = tickPoison(nextState);
  // pushEvent 已经在 beginTurn 之外；这里追加 turn-start
  return {
    ...nextState,
    log: [
      ...nextState.log,
      {
        type: "turn-start",
        playerId: players[next].id,
        round: newRound,
      },
    ],
  };
}

// 便利：完整跳过弃牌（手牌不超限）
export function skipDiscardIfUnderLimit(state: GameState): GameState {
  const player = state.players[state.currentPlayerIdx];
  if (player.hand.length <= effectiveHandLimit(player)) {
    // 直接进入 endTurn
    return endTurn({ ...state, phase: "discard" });
  }
  return state;
}
